import { useQuery, useQueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query'
import { GC_TIME, STALE_TIMES, type LookupTier } from '@/shared/queries/lookupStaleTimes'

interface UseLookupOptions<TData, TItem> {
  queryKey: QueryKey
  queryFn: () => Promise<TData>
  tier: LookupTier
  enabled?: boolean
  /** e.g. `keepPreviousData`, so a key change (new ids) doesn't blank the UI while it refetches. */
  placeholderData?: (previousData: TData | undefined) => TData | undefined
  seed?: {
    items: (data: TData) => TItem[]
    key: (item: TItem) => QueryKey
  }
}

export const useLookup = <TData, TItem = unknown>(
  options: UseLookupOptions<TData, TItem>,
): UseQueryResult<TData> => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: options.queryKey,
    enabled: options.enabled,
    staleTime: STALE_TIMES[options.tier],
    gcTime: GC_TIME,
    // Cast: react-query's PlaceholderDataFunction generic threads extra type
    // params (error/query-key types) this thin wrapper doesn't track; the
    // narrower signature above is all callers need.
    placeholderData: options.placeholderData as never,
    queryFn: async () => {
      const data = await options.queryFn()
      if (options.seed) {
        for (const item of options.seed.items(data)) {
          queryClient.setQueryData(options.seed.key(item), item)
        }
      }
      return data
    },
  })
}
